import React from 'react';
import * as stylex from '@stylexjs/stylex';
import { useCheckbox, VisuallyHidden, type AriaCheckboxProps } from 'react-aria';
import { useToggleState } from 'react-stately';
import '../theme/Colors.stylex';
import { ColorBrand, ColorNeutral, ColorNeutralInverse } from '../theme/Colors.stylex';

// NOTE: react-aria checkbox (a11y/keyboard) + tri-state, with built-in press isolation
// for use inside a pressable row: a native listener swallows the pointer events
// (React stopPropagation can't stop react-aria's usePress) and drives the toggle.

export type Tone = 'brand';
export type CheckboxProps = {
  ariaProps?: AriaCheckboxProps;
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  tone?: Tone;
  accent?: boolean;
  size?: number;
  ariaLabel?: string;
  hovered?: boolean;
  isolatePress?: boolean; // prevent the click from reaching a pressable ancestor
  onChange?: (next: boolean) => void;
};
type DisplayState = 'checked' | 'indeterminate' | 'unchecked';
type State = 'default' | 'hovered' | 'pressed' | 'disabled';
export type CheckboxTickProps = {
  size?: number;
  color?: string;
  displayed?: boolean;
  indeterminate?: boolean;
};
export const CheckboxTick = React.memo(function CheckboxTick({
  size = 15,
  color = ColorBrand.iconDefault,
  displayed = true,
  indeterminate = false
}: CheckboxTickProps) {
  const shapeRef = React.useRef<'checked' | 'indeterminate'>(indeterminate ? 'indeterminate' : 'checked');
  if (displayed) shapeRef.current = indeterminate ? 'indeterminate' : 'checked';
  return <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden focusable={false} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{
    transition: 'transform 110ms ease, opacity 90ms ease',
    transform: displayed ? 'scale(1)' : 'scale(0.6)',
    opacity: displayed ? 1 : 0
  }}>
      <path d={ICON_PATHS[shapeRef.current]} pathLength={1} style={{
      strokeDasharray: 1,
      strokeDashoffset: displayed ? 0 : 1,
      transition: 'stroke-dashoffset 140ms ease 30ms'
    }} />
    </svg>;
});
export const Checkbox = React.memo(function CheckboxComponent({
  ariaProps,
  checked = false,
  indeterminate = false,
  disabled = false,
  tone = 'brand',
  accent = false,
  size = 15,
  ariaLabel = 'checkbox',
  hovered = false,
  isolatePress = true,
  onChange
}: CheckboxProps) {
  const props: AriaCheckboxProps = {
    isSelected: checked,
    isIndeterminate: indeterminate,
    isDisabled: disabled,
    'aria-label': ariaLabel,
    onChange,
    ...ariaProps
  };
  const isChecked = props.isSelected ?? false;
  const isIndeterminate = props.isIndeterminate ?? false;
  const isDisabled = props.isDisabled ?? false;
  const state = useToggleState(props);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const {
    inputProps
  } = useCheckbox(props, state, inputRef);
  const [pressed, setPressed] = React.useState(false);
  const labelRef = React.useRef<HTMLLabelElement>(null);
  const toggle = React.useRef(() => {});
  toggle.current = () => {
    if (!isDisabled) props.onChange?.(!isChecked);
  };
  React.useEffect(() => {
    const el = labelRef.current;
    if (!el) return;
    const swallow = (e: Event) => {
      if (isolatePress) e.stopImmediatePropagation();
    };
    const onDown = (e: Event) => {
      swallow(e);
      if (!isDisabled) setPressed(true);
    };
    const onUp = (e: Event) => {
      swallow(e);
      setPressed(false);
    };
    const onClick = (e: Event) => {
      swallow(e);
      e.preventDefault();
      toggle.current();
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('mousedown', swallow);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointerleave', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('click', onClick);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('mousedown', swallow);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointerleave', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('click', onClick);
    };
  }, [isolatePress, isDisabled]);
  const display: DisplayState = isIndeterminate ? 'indeterminate' : isChecked ? 'checked' : 'unchecked';
  const boxState: State = isDisabled ? 'disabled' : pressed ? 'pressed' : hovered ? 'hovered' : 'default';
  const isActive = display !== 'unchecked';
  const iconColor = isDisabled ? ColorBrand.fillDefault : ColorNeutralInverse.iconAccent;
  return <label ref={labelRef} {...boxStyleProps(tone, boxState, isActive, accent)} style={{
    width: size,
    height: size
  }}>
      <VisuallyHidden>
        <input {...inputProps} ref={inputRef} />
      </VisuallyHidden>
      <CheckboxTick size={size} color={iconColor} displayed={isActive} indeterminate={isIndeterminate} />
    </label>;
});
const ICON_PATHS = {
  checked: 'M3.5 8.5 6.5 11.5 12.5 5',
  indeterminate: 'M4 8 12 8'
};

// MARK: style
// ===========
// unchecked: white background, colored border (per state)
const brandUncheckedStyles = {
  default: {
    "borderColor-kVAM5u": "borderColor-x5bw2fz borderColor-x10z5a45",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:204"
  },
  hovered: {
    "borderColor-kVAM5u": "borderColor-x8qcncr",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:210"
  },
  pressed: {
    "borderColor-kVAM5u": "borderColor-x8qcncr",
    "borderWidth-kMzoRj": "borderWidth-xdh2fpr",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:211"
  },
  disabled: {
    "borderColor-kVAM5u": "borderColor-xv9nw99",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:212"
  }
};
// checked/indeterminate: white icon, colored background (per state)
const brandActiveStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-x1ryq1pb backgroundColor-x8dlulk",
    "borderColor-kVAM5u": "borderColor-x39ameu borderColor-x1m11rg5",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:216"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1bqw7n2",
    "borderColor-kVAM5u": "borderColor-x7f5cy9",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:226"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xtsy7rd",
    "borderColor-kVAM5u": "borderColor-x1xd0h2v",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:230"
  },
  disabled: {
    "backgroundColor-kWkggS": "backgroundColor-x1ss2p8f",
    "borderColor-kVAM5u": "borderColor-x3cyibe",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:234"
  }
};
const boxStyleProps = (_tone: Tone, state: State, isActive: boolean, accent: boolean) => stylex.props(styles.root, isActive ? brandActiveStyles[state] : brandUncheckedStyles[state], !isActive && state === 'default' && accent && styles.uncheckedAccent, state === 'disabled' && styles.disabled);
const styles = {
  root: {
    "position-kVAEAm": "position-x1n2onr6",
    "display-k1xSpc": "display-x3nfvp2",
    "flex-kUk6DE": "flex-x3psx0u",
    "alignItems-kGNEyG": "alignItems-x6s0dn4",
    "justifyContent-kjj79g": "justifyContent-xl56j7k",
    "cursor-kkrTdU": "cursor-xt0e3qv",
    "boxSizing-kB7OPa": "boxSizing-x9f619",
    "backgroundColor-kWkggS": "backgroundColor-x12peec7",
    "borderRadius-kaIpWk": "borderRadius-x1cum3z5",
    "borderStyle-ksu8eU": "borderStyle-x1y0btm7",
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "transition-kmkexE": "transition-x1w262v8",
    "outlineStyle-k3XXqK": "outlineStyle-xaatb59",
    "outlineOffset-kInvED": "outlineOffset-x1hl8ikr",
    "outlineWidth-kMeerF": "outlineWidth-x1k57tk5 outlineWidth-xoxpeok",
    "outlineColor-kjBf7l": "outlineColor-x1uczgqu outlineColor-xmdurkk",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:254"
  },
  uncheckedAccent: {
    "borderColor-kVAM5u": "borderColor-x19mpen5 borderColor-x10z5a45",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:275"
  },
  disabled: {
    "cursor-kkrTdU": "cursor-x1h6gzvc",
    $$css: "wino-gc:src/resources/selection-and-input/Checkbox.tsx:281"
  }
};